import { Header, TypeOfTableData } from '../types/generic-table.model';
import KPISelection from '../types/kpi-selection.enum';
import {
  IDataRow,
  ISimTableColumnMetadata
} from '../types/simulation-view-table.model';
import { calculateAbsoluteChange } from './number-utils';

/* eslint-disable no-param-reassign */
export function getAccessorFields(displayColumns: ISimTableColumnMetadata[]) {
  return displayColumns
    .filter((metadata) => metadata.is_active)
    .reduce(
      (previousValue, currentValue) => {
        if (currentValue?.field_alias === 'Market') {
          previousValue[KPISelection.RETAILER_KPI] = currentValue.field_name;
        } else if (currentValue?.field_alias === 'PPG') {
          previousValue[KPISelection.OWN_KPI] = currentValue.field_name;
        }
        return previousValue;
      },
      {
        [KPISelection.OWN_KPI]: null,
        [KPISelection.RETAILER_KPI]: null
      }
    );
}
/* eslint-enable no-param-reassign */

export default function createEmptyRowObjectWithDefaultKeys(
  rowToUpdate: IDataRow,
  displayColumns: ISimTableColumnMetadata[]
) {
  const updatedRow: IDataRow = displayColumns
    .map((column) => column.field_name)
    .reduce(
      (previousValue, key) => ({
        ...previousValue,
        ...{ [key]: rowToUpdate[key] }
      }),
      {}
    );
  return updatedRow;
}

/**
 * Function to change the values of the columns that are linked to current column
 * @param rowToUpdate - Data row whose property needs to be changed
 * @param linkColumnMetadata - Metadata to find the properties in row that needs to be changed
 * @param newValue - The new % value entered by user
 */
/* eslint-disable no-param-reassign */
export function updateLinkedFieldNewValue(
  rowToUpdate: IDataRow,
  linkColumnMetadata: ISimTableColumnMetadata,
  newValue: number
) {
  const keyToUpdate = linkColumnMetadata.is_perc_active
    ? linkColumnMetadata.perc_change_value_field_name
    : linkColumnMetadata.additional_value_field_name;
  rowToUpdate[keyToUpdate] = newValue;
  if (linkColumnMetadata.is_new_active) {
    rowToUpdate[linkColumnMetadata.new_value_field_name] =
      calculateAbsoluteChange(
        newValue,
        rowToUpdate[linkColumnMetadata.field_name] as number
      );
  }
}

/**
 * Function to change the new value based upon the percentage change of the linked field
 * @param rowToUpdate - Data row whose property needs to be changed
 * @param linkColumnMetadata - Metadata to find the properties in row that needs to be changed
 * @param percentChange - The change in percentage of new value
 */
export function updateLinkedFieldPercOrAdditionalChange(
  rowToUpdate: IDataRow,
  linkColumnMetadata: ISimTableColumnMetadata,
  percentChange: number
) {
  if (linkColumnMetadata.is_perc_active) {
    rowToUpdate[linkColumnMetadata.perc_change_value_field_name] =
      percentChange;
  }
  if (linkColumnMetadata.is_additional_active) {
    rowToUpdate[linkColumnMetadata.additional_value_field_name] = percentChange;
  }
  const newValue = calculateAbsoluteChange(
    percentChange,
    rowToUpdate[linkColumnMetadata.field_name] as number
  );
  if (linkColumnMetadata.is_new_active) {
    rowToUpdate[linkColumnMetadata.new_value_field_name] = newValue;
  }
}
/* eslint-enable no-param-reassign */

/**
 *Filters through `dataRows` and finds out which property of each row has changed
 * @param dataRows List of all the rows in the simulation table
 * @param inputColumnsMetadata Metadata required to find what fields to check for in each row
 * @param displayColumns Metadata to add default fields to `updatedRow` object containing only the properties that are changed
 * @returns A new list containing only the rows that has changed
 */
export function getUpdatedRowsFromDataset(
  dataRows: IDataRow[],
  inputColumnsMetadata: ISimTableColumnMetadata[],
  displayColumns: ISimTableColumnMetadata[]
) {
  const { [KPISelection.RETAILER_KPI]: marketField } =
    getAccessorFields(displayColumns);
  return dataRows
    .filter((row) => !!row[marketField])
    .filter((row) =>
      inputColumnsMetadata.some(
        (columnMetadata) =>
          // Check to see if the percentage change value is non zero
          (columnMetadata.is_perc_active &&
            row[columnMetadata.perc_change_value_field_name]) ||
          // Check to see if the additional change value is non zero
          (columnMetadata.is_additional_active &&
            row[columnMetadata.additional_value_field_name]) ||
          // Check to see if any checkbox is set to true
          (columnMetadata.is_checkbox_field &&
            row[columnMetadata.checkbox_value_field_name])
      )
    )
    .map((row) => {
      const updatedRow = createEmptyRowObjectWithDefaultKeys(
        row,
        displayColumns
      );
      inputColumnsMetadata.forEach((columnMetadata) => {
        if (
          columnMetadata.is_perc_active &&
          row[columnMetadata.perc_change_value_field_name]
        ) {
          updatedRow[columnMetadata.perc_change_value_field_name] = row[
            columnMetadata.perc_change_value_field_name
          ] as number;
        }
        if (
          columnMetadata.is_additional_active &&
          row[columnMetadata.additional_value_field_name]
        ) {
          updatedRow[columnMetadata.additional_value_field_name] = row[
            columnMetadata.additional_value_field_name
          ] as number;
        }
        if (
          columnMetadata.is_checkbox_field &&
          row[columnMetadata.checkbox_value_field_name]
        ) {
          updatedRow[columnMetadata.checkbox_value_field_name] =
            row[columnMetadata.checkbox_value_field_name];
        }
      });
      return updatedRow;
    });
}

/**
 *Filters through `dataRows` and finds out which property of each row has changed
 * @param dataRows List of all the rows in the simulation table
 * @param inputColumnsMetadata Metadata required to find what fields to check for in each row
 * @param displayColumns Metadata to add default fields to `updatedRow` object containing only the properties that are changed
 * @returns A new list containing all the rows
 */
export function getCompleteRowObjectsForUpdatedRows(
  dataRows: IDataRow[],
  inputColumnsMetadata: ISimTableColumnMetadata[],
  displayColumns: ISimTableColumnMetadata[]
) {
  const { [KPISelection.RETAILER_KPI]: marketField } =
    getAccessorFields(displayColumns);
  return dataRows.filter((row) =>
    inputColumnsMetadata.some(
      (columnMetadata) =>
        // Check to see if the percentage change value is non zero
        (columnMetadata.is_perc_active &&
          row[columnMetadata.perc_change_value_field_name]) ||
        // Check to see if the additional change value is non zero
        (columnMetadata.is_additional_active &&
          row[columnMetadata.additional_value_field_name]) ||
        // Check to see if any checkbox is set to true
        (columnMetadata.is_checkbox_field &&
          row[columnMetadata.checkbox_value_field_name])
    )
  );
}

/**
 * Parses the given string formula and returns the properties of data rows used in the formula
 * @param formula Formula to check for fields
 * @returns list of fields
 */
export function extractFieldsFromFormulae(formula: string): string[] {
  if (!formula) {
    return [];
  }
  // eslint-disable-next-line prefer-regex-literals
  const regex = new RegExp('(?<={).*?(?=})', 'g');
  const fields = [];
  let results = null;
  do {
    results = regex.exec(formula);
    if (results) {
      fields.push(results[0]);
    }
  } while (results);
  return fields;
}

/**
 * Filters the given entire set of columns to only collect those columns which
 * need to be displayed in the table for the given view
 * @param typeOfColumnsToFilter What type of columns to look for - INPUT, OUTPUT, DISPLAY
 * @param columns set of columns
 * @param toggledView The view for which column needs to be filtered for (own kpi or retailer kpi view). If not provided, not checked for it.
 * @returns set of filtered columns
 */
export function filterColumnsForToggleView(
  typeOfColumnsToFilter: TypeOfTableData[],
  columns: Header[],
  toggledView: string
) {
  if (!Array.isArray(columns)) return [];


  return typeOfColumnsToFilter
    .map((typeOfColumn) => {
      // Filter columns by section type
      return columns
        .filter((column: Header) => {
          // Basic validation - check if column has the required properties
          if (!column || !column.parent) {
            return false;
          }
          
          // Check if the parent column matches the section type and is visible
          if (column.parent.section !== typeOfColumn || !column.parent.is_visible) {
            return false;
          }

          // Check if parent has the toggle tag or if toggle view is not specified
          const parentMatchesToggle = !toggledView || 
            (column.parent.toggle_tag && column.parent.toggle_tag.includes(toggledView));

          // Check if any children match the toggle view
          const hasMatchingChildren = column.children && 
            Array.isArray(column.children) &&
            column.children.some(child => 
              child.parent && 
              child.parent.is_visible === true && 
              (!toggledView || (child.parent.toggle_tag && child.parent.toggle_tag.includes(toggledView)))
            );

          // Include this column if parent matches toggle or if it has matching children
          return parentMatchesToggle || hasMatchingChildren;
        })
        .map(column => {
          // Create a new column with filtered children
          if (column.children && Array.isArray(column.children) && toggledView) {
            return {
              parent: column.parent,
              children: column.children.filter(child => 
                child.parent && 
                child.parent.is_visible === true && 
                child.parent.toggle_tag && 
                child.parent.toggle_tag.includes(toggledView)
              )
            } as Header;
          }
          return column;
        });
    })
    .flat(1)
    .sort((a, b) => a.parent.order - b.parent.order);
}

// export function filterColumnsForToggleView(
//   typeOfColumnsToFilter: TypeOfTableData[],
//   columns: Header[],
//   toggledView: string
// ) {
//   if (!Array.isArray(columns)) return [];
//   return typeOfColumnsToFilter
//     .map((typeOfColumn) =>
//       columns.filter(
//         (column: Header) =>
//           column.parent.section === typeOfColumn &&
//           column.parent.is_visible === true &&
//           column.parent.child_field === '' &&
//           (toggledView ? column.parent.toggle_tag.includes(toggledView) : true)
//       ))
//   .flat(1)
//   .sort((a, b) => a.parent.order - b.parent.order);
// }

export function filterColumnsForSimResults(
  typeOfColumnsToFilter: TypeOfTableData[],
  columns: any,
  toggledView: string
) {
  return typeOfColumnsToFilter
    .map((typeOfColumn) =>
      columns
        .filter((column: Header) =>
          // Filter based on the parent conditions
          column.parent.section === typeOfColumn &&
          column.parent.is_visible === true &&
          column.parent.child_field === '' &&
          (toggledView ? column.parent.toggle_tag.includes(toggledView) : true)
        )
        .map((filteredColumn) => {
          // Start with an empty array to hold child columns
          const result: any[] = [];

          // If the column has children, add them to the result
          if (filteredColumn.children && filteredColumn.children.length > 0) {
            result.push(...filteredColumn.children);  // Add all children to the result array
          }
          
          return result;  // Return only children (not the parent)
        })
    )
    .flat(2)  // Flatten the array to include only the children columns
    .sort((a, b) => a.parent.order - b.parent.order);  // Sort by the parent's order (though the parent is not included)
}

/**
 * Create the output columns visibility state for the table
 * @param columns Set of output columns of the table
 * @param toggledView The view that is toggled to (% change or absolute change)
 * @returns an object representing each of the column's field name as key and its visibility in boolean
 * `{
 *  'nsv_cal_chg_abs': true
 * }`
 */
export function createColumnVisibilityStateForOutputColumns(
  columns: Header[],
  toggledView: string
) {
  return columns
    .filter(
      (header) =>
        header && header.parent.parent_field && header.parent.is_visible
    )
    .map((header) => ({
      fieldName: header.parent.parent_field,
      fieldVisibility: header.parent.output_toggle_tag.includes(toggledView)
    }))
    .reduce(
      (prev, curr) => ({
        ...prev,
        [curr.fieldName]: curr.fieldVisibility
      }),
      {}
    );
}
